<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { computed, ref, watch } from 'vue'
import { cn } from '../utils'
import { usePromptInput } from './context'

defineOptions({
  inheritAttrs: false,
})

interface Props {
  class?: HTMLAttributes['class']
  placeholder?: string
  name?: string
  disabled?: boolean
  modelValue?: string
  rows?: number | string
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'What would you like to know?',
  name: 'message',
  rows: 2,
})

const { textInput, setTextInput, submitForm, addFiles, files, removeFile } =
  usePromptInput()
const isComposing = ref(false)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

watch(
  () => props.modelValue,
  (value) => {
    if (value !== undefined && value !== textInput.value) {
      setTextInput(value)
    }
  },
  { immediate: true }
)

function handleKeyDown(e: KeyboardEvent) {
  if (e.key === 'Enter') {
    if (isComposing.value || e.shiftKey) return

    e.preventDefault()
    submitForm()
  }

  if (
    e.key === 'Backspace' &&
    textInput.value === '' &&
    files.value.length > 0
  ) {
    const lastFile = files.value[files.value.length - 1]
    if (lastFile) {
      removeFile(lastFile.id)
    }
  }
}

function handlePaste(e: ClipboardEvent) {
  const items = e.clipboardData?.items
  if (!items) return

  const pastedFiles: File[] = []
  for (const item of Array.from(items)) {
    if (item.kind === 'file') {
      const file = item.getAsFile()
      if (file) pastedFiles.push(file)
    }
  }

  if (pastedFiles.length > 0) {
    e.preventDefault()
    addFiles(pastedFiles)
  }
}

const modelValue = computed({
  get: () => textInput.value,
  set: (val) => {
    setTextInput(val)
    emit('update:modelValue', val)
  },
})
</script>

<template>
  <textarea
    v-model="modelValue"
    :placeholder="props.placeholder"
    :name="props.name"
    :rows="props.rows"
    :disabled="props.disabled"
    :class="
      cn(
        'max-h-48 min-h-16 w-full resize-none border-0 bg-transparent px-3 py-3 text-sm text-gray-900 shadow-none outline-none placeholder:text-gray-400 focus:ring-0 disabled:cursor-not-allowed disabled:opacity-60 dark:text-gray-100 dark:placeholder:text-gray-500',
        props.class
      )
    "
    v-bind="$attrs"
    @keydown="handleKeyDown"
    @paste="handlePaste"
    @compositionstart="isComposing = true"
    @compositionend="isComposing = false"
  />
</template>
